export const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i
export const PHONE_PATTERN = /\+?\d[\d\s().-]{8,}\d/
export const BLOCKED_KEYS = new Set([
  'email',
  'phone',
  'address',
  'first_name',
  'last_name',
  'full_name',
  'message',
  'comment',
  'input_value',
  'field_value',
  'raw_value',
])

function isObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

export function normalizeText(value) {
  if (value == null) {
    return ''
  }

  return String(value).replace(/\s+/g, ' ').trim()
}

export function sanitizeValue(value) {
  if (typeof value !== 'string') {
    return value
  }

  const normalized = normalizeText(value)

  if (!normalized) {
    return ''
  }

  if (EMAIL_PATTERN.test(normalized) || PHONE_PATTERN.test(normalized)) {
    return '[redacted]'
  }

  return normalized
}

export function sanitizePayload(payload) {
  if (!isObject(payload)) {
    return {}
  }

  return Object.entries(payload).reduce((acc, [key, value]) => {
    if (BLOCKED_KEYS.has(key)) {
      return acc
    }

    if (value == null) {
      return acc
    }

    if (Array.isArray(value)) {
      acc[key] = value.map(item => sanitizeValue(item))
      return acc
    }

    if (isObject(value)) {
      acc[key] = sanitizePayload(value)
      return acc
    }

    acc[key] = sanitizeValue(value)
    return acc
  }, {})
}

export function buildBasePayload(config = {}) {
  const location = window.location || {}

  return {
    site_handle: config.siteHandle || 'default',
    environment: config.environment || 'dev',
    page_path: location.pathname || '',
    page_title: normalizeText(document.title || ''),
    page_url: location.href || '',
    event_source: 'frontend',
    timestamp_ms: Date.now(),
  }
}

export function parseFormConfig(form) {
  if (!form) {
    return {}
  }

  const rawConfig = form.getAttribute('data-fui-form')

  if (!rawConfig) {
    return {}
  }

  try {
    return JSON.parse(rawConfig)
  } catch {
    return {}
  }
}

export function parseFormId(value) {
  if (value == null) {
    return null
  }

  const parsed = Number.parseInt(String(value), 10)
  return Number.isNaN(parsed) ? null : parsed
}

export function extractFormMetadata(form) {
  if (!form) {
    return {}
  }

  const config = parseFormConfig(form)
  const dataset = form.dataset || {}

  const formHandle = dataset.formHandle || config.formHandle || ''
  const formId = parseFormId(config.formId)
  const formTitle = dataset.formTitle || formHandle
  const formSubmitMethod = dataset.formSubmitMethod || config?.settings?.submitMethod || ''
  const formSubmitAction = dataset.formSubmitAction || config?.settings?.submitAction || ''

  return {
    form_handle: normalizeText(formHandle),
    form_id: formId,
    form_title: normalizeText(formTitle),
    form_dom_id: normalizeText(form.id || config.formHashId || ''),
    form_submit_method: normalizeText(formSubmitMethod),
    form_submit_action: normalizeText(formSubmitAction),
  }
}

export function pushDataLayerEvent(payload, config = {}) {
  if (config.debugTracking) {
    // eslint-disable-next-line no-console
    console.debug('[tracking]', sanitizePayload(payload))
  }

  const eventPayload = sanitizePayload({
    ...buildBasePayload(config),
    ...payload,
  })

  if (!eventPayload.event) {
    return false
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(eventPayload)

  return true
}
